import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ScrollText, Save, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = '';

// Canonical document order in the UI. The backend ensures these are seeded.
const DOC_SLUGS = ['terms-of-use', 'community-guidelines', 'privacy-policy'];

export function AdminGovernanceTab() {
  const [docs, setDocs] = useState([]);
  const [activeSlug, setActiveSlug] = useState(DOC_SLUGS[0]);
  const [editorTitle, setEditorTitle] = useState('');
  const [editorBody, setEditorBody] = useState('');
  const [originalTitle, setOriginalTitle] = useState('');
  const [originalBody, setOriginalBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/governance`, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        setDocs(d.documents || []);
      }
    } catch { toast.error('Failed to load policies'); }
    setLoading(false);
  };

  const loadDoc = async (slug) => {
    setActiveSlug(slug);
    try {
      const res = await fetch(`${BACKEND_URL}/api/governance/${slug}`, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        setEditorTitle(d.title || '');
        setEditorBody(d.body || '');
        setOriginalTitle(d.title || '');
        setOriginalBody(d.body || '');
      }
    } catch { toast.error('Failed to load document'); }
  };

  useEffect(() => {
    loadAll().then(() => loadDoc(DOC_SLUGS[0]));
  }, []);

  const dirty = editorTitle !== originalTitle || editorBody !== originalBody;

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/governance/${activeSlug}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: editorTitle, body: editorBody }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Failed'); }
      toast.success('Policy saved');
      setOriginalTitle(editorTitle);
      setOriginalBody(editorBody);
      loadAll();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  return (
    <div className="space-y-4" data-testid="admin-governance-tab">
      <div>
        <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
          <ScrollText className="h-5 w-5 text-brand-navy" /> Governance Documents
        </h2>
        <p className="text-xs text-slate-500 mt-0.5">
          Edit the Terms of Use, Community Guidelines and Privacy Policy. Markdown is supported — use # for headings, **bold**, _italic_, and [link text](https://…).
        </p>
      </div>

      {/* Document selector */}
      <Card className="p-2 flex flex-wrap gap-2">
        {DOC_SLUGS.map(slug => {
          const meta = docs.find(d => d.slug === slug) || {};
          const isActive = activeSlug === slug;
          return (
            <Button key={slug} variant={isActive ? 'default' : 'ghost'} size="sm"
              onClick={() => loadDoc(slug)}
              data-testid={`governance-tab-${slug}`}
              className={`text-xs ${isActive ? 'bg-brand-navy text-white' : ''}`}>
              {meta.title || slug.replace(/-/g, ' ')}
              {meta.version ? <span className="ml-2 text-[10px] opacity-70">v{meta.version}</span> : null}
            </Button>
          );
        })}
      </Card>

      {/* Editor */}
      {loading ? (
        <Card className="p-8 text-center text-sm text-slate-400">Loading…</Card>
      ) : (
        <Card className="p-4 md:p-6 space-y-4">
          <div>
            <Label>Title</Label>
            <Input value={editorTitle}
              onChange={e => setEditorTitle(e.target.value)}
              className="mt-1" data-testid="governance-title-input" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Body (Markdown)</Label>
              <Link to={`/governance/${activeSlug}`} target="_blank"
                className="text-[11px] text-brand-navy hover:underline inline-flex items-center gap-1"
                data-testid="governance-preview-link">
                Preview public page <ExternalLink className="h-3 w-3" />
              </Link>
            </div>
            <Textarea value={editorBody}
              onChange={e => setEditorBody(e.target.value)}
              rows={22}
              className="font-mono text-sm"
              data-testid="governance-body-input" />
            <p className="text-[10px] text-slate-400 mt-1">
              Supported: `# H1`, `## H2`, `### H3`, `**bold**`, `_italic_`, `[link](url)`. Plain paragraphs separated by a blank line.
            </p>
          </div>

          <div className="flex items-center justify-end gap-2">
            {dirty && <span className="text-[11px] text-amber-600 mr-auto">Unsaved changes</span>}
            <Button variant="outline" onClick={() => { setEditorTitle(originalTitle); setEditorBody(originalBody); }}
              disabled={!dirty}>
              Discard
            </Button>
            <Button onClick={handleSave} disabled={!dirty || saving}
              className="bg-brand-navy text-white"
              data-testid="governance-save-btn">
              <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
